
import { useState, useCallback } from 'react';
import { MessageType } from '@/types/assistant';
import { v4 as uuidv4 } from 'uuid';

interface ErrorBoundaryState {
  hasError: boolean;
  error: string | null;
}

interface UseErrorBoundaryProps {
  currentSessionId: string | null;
  userId: string | null;
  onMessageAdd?: (message: MessageType) => void;
  onMessageRemove?: (messageId: string) => void;
}

export const useErrorBoundary = ({
  currentSessionId,
  userId,
  onMessageAdd,
  onMessageRemove
}: UseErrorBoundaryProps) => {
  const [state, setState] = useState<ErrorBoundaryState>({
    hasError: false,
    error: null,
  });

  const handleError = useCallback((error: any) => {
    setState({ hasError: true, error: error.message || 'An unexpected error occurred.' });
  }, []);

  const resetError = useCallback(() => {
    setState({ hasError: false, error: null });
  }, []);

  const createErrorMessage = useCallback((error: string, sessionId: string, userId: string): MessageType => {
    return {
      id: uuidv4(),
      content: `I apologize, but I encountered an error: ${error}. Please try again or contact support if the issue persists.`,
      role: 'assistant',
      timestamp: new Date(),
      session_id: sessionId,
      user_id: userId,
      suggestions: [],
      feedback: null,
      is_streaming: false, // Error messages are never streaming
    };
  }, []);

  const handleUsageLimitError = useCallback((error: Error): boolean => {
    if (error.message.includes('limit exceeded') || error.message.includes('usage limit')) {
      handleError(error);
      return true;
    }
    return false;
  }, [handleError]);

  const handleAiServiceError = useCallback((error: Error, requestId: string) => {
    console.error('AI Service Error:', { error: error.message, requestId });
    handleError(error);
  }, [handleError]);

  const handleGenericError = useCallback((error: unknown, context: string, requestId?: string) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`Generic Error in ${context}:`, { error: errorMessage, requestId });
    handleError(new Error(errorMessage));
  }, [handleError]);

  const handleMessageSaveError = useCallback((error: unknown, messageId: string, requestId?: string) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Message Save Error:', { error: errorMessage, messageId, requestId });
    handleError(new Error(`Failed to save message: ${errorMessage}`));
  }, [handleError]);

  const recoverFromError = useCallback((error: unknown, context: string, requestId?: string) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`Recovering from error in ${context}:`, { error: errorMessage, requestId });
    
    if (currentSessionId && userId) {
      const errorMsg = createErrorMessage(errorMessage, currentSessionId, userId);
      onMessageAdd?.(errorMsg);
    }
    
    handleError(new Error(errorMessage));
  }, [currentSessionId, userId, createErrorMessage, onMessageAdd, handleError]);

  return {
    ...state,
    handleError,
    resetError,
    createErrorMessage,
    handleUsageLimitError,
    handleAiServiceError,
    handleGenericError,
    handleMessageSaveError,
    recoverFromError
  };
};
